<template>
  <div class="flex justify-center">
    <div v-if="showConfirmModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div class="w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <h3 class="mb-4 text-xl font-semibold text-secondary-600">Confirm Order</h3>
        <p class="mb-6 text-gray-600">
          This will place a <span class="whitespace-nowrap">{{ total | currency }}</span> hold on your card ending in <span class="whitespace-nowrap">{{ card }}</span> 
          and place your coding assignments in our queue to be coded.
        </p>
        <div class="flex justify-between">
          <button 
            @click="confirmSubmitOrder" 
            ref="confirmButton"
            class="px-4 py-2 text-white transition-colors bg-logo hover:bg-logo-light rounded-lg"
          >
            Confirm
          </button>
          <button 
            @click="showConfirmModal = false" 
            class="link link-red"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    <div v-if="updatePaymentMethod" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div class="w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <h3 class="mb-4 text-xl font-semibold text-secondary-600">Update Card</h3>
        <p class="mb-6 text-gray-600">
          Would you like to update your payment information?
        </p>
        <div class="flex justify-between">
          <button class="btn btn-red" @click="updatePaymentMethod = false">No</button>
          <button class="btn btn-blue" @click="triggerUpdatePaymentMethod">Yes</button>
        </div>
      </div>
    </div>
    <div v-if="showCardComponent" class="z-50">
      <store-card :agency="agency" :stripeConfigs="stripeConfigs" :automatic-trigger="true" />
    </div>
    <div v-if="charging" class="flex justify-center p-4 text-logo-dark font-semibold rounded-lg bg-white shadow-lg h-24">
      Please Wait While Your Order Is Being Processed, You Should Be Directed To Your
      Dashboard Soon.
    </div>
    <div class="w-full" v-else>
      <div class="flex justify-between">
        <h2 class="text-secondary-600">New Assignment(s)</h2>
        <div class="self-end">
          <a :href="'/agencies/' + agency_id" class="text-xs link link-red"
            >Leave Order Page</a
          >
        </div>
      </div>
      <div class="p-4 bg-white rounded-lg shadow-lg md:flex  md:justify-between">
        <div class="p-4 pt-0 sm:w-full md:w-1/3 xl:w-2/5">
          <!-- <form method="POST" enctype="multipart/form-data" name="formName"> -->
          <div class="pt-0 pb-4 pr-4">
            <div class="relative">
              <input
                type="text"
                class="form-input input float-input peer"
                v-model="assign_data.name"
                placeholder="name"
                id="name"
              />
              <label for="name" class="float-label">Name</label>
            </div>
            <div class="relative mt-4">
              <select
                class="input float-input peer"
                v-model="assign_data.document_type"
                id="document"
                placeholder="document"
              >
                <option></option>
                <option>SOC</option>
                <option>ROC</option>
                <option>Recert</option>
                <option>Follow-up</option>
                <option>SNA</option>
              </select>
              <label
                for="document"
                class="float-label"
                :class="{
                  'font-normal': assign_data.document_type == '',
                }"
                >Document Type</label
              >
            </div>
            <div class="relative mt-4">
              <input
                type="date"
                class="form-input input float-input peer"
                v-model="assign_data.date"
                placeholder="Document Date"
                id="date"
              />
              <label for="date" class="float-label">Document Date</label>
            </div>
            <div class="pt-4 hidden">
              <div class="flex justify-start">
                <div class="pr-4 font-bold opacity-50">Coming Soon</div>
                <div>
                  <label
                    class="opacity-50"
                    for="urgent"
                    title="moves the document to the top of our que"
                    >Urgent</label
                  >
                  <input
                    id="urgent"
                    type="checkbox"
                    v-model="assign_data.urgent"
                    disabled
                  />
                </div>
                <div class="px-12">
                  <label
                    class="opacity-50"
                    for="pro"
                    title="Only coders with 5+ years of experience can code this document"
                    >Pro</label
                  >
                  <input id="pro" type="checkbox" v-model="assign_data.pro" disabled />
                </div>
              </div>
            </div>
            <div class="mt-4">
              <div class="relative">
                <textarea
                  id="comments"
                  placeholder="If there are specific instructions for this chart, place them here"
                  class="form-input input float-input peer"
                  v-model="assign_data.comments"
                  rows="3"
                ></textarea>
                <label for="comments" class="float-label"
                  >If there are specific instructions for this chart, place them here</label
                >
                <p class="text-xs text-gray-500 text-justify">
                  You may add attachments to this assignment after adding it to the order by clicking "Attachments / Remove Items" at the top of the Order Summary pane and clicking the <i class="fas fa-paperclip text-logo-alt2"></i> icon for that assignment.
                </p>
              </div>
            </div>

            <div class="pt-8 -mb-8 text-center">
              <button type="button" class="better-btn bg-logo-alt2 hover:bg-logo-alt2-dark text-white px-20" @click="assign">Add</button>
            </div>


            <div class="mt-16">
              <i
                class="fas fa-question-circle float-right m-2
                text-logo-alt2 hover:text-logo-alt2-dark cursor-pointer"
                @click="showCsvExample = true"
              ></i>
              <div
                class="px-1 xl:px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-logo-alt2 transition-colors"
                @dragover.prevent
                @drop.prevent="handleFileDrop"
              >
                <div class="text-center">
                  <input
                    type="file"
                    ref="csvInput"
                    accept=".csv"
                    class="hidden"
                    @change="handleCsvUpload"
                  />
                  <button
                    @click="triggerFileInput"
                    class="mb-2 text-logo-alt2 hover:text-logo-alt2-dark"
                  >
                    <i class="fas fa-file-csv text-xl"></i>
                    <span class="ml-2">Upload CSV</span>
                  </button>
                  <p class="text-sm text-gray-500 flex items-center justify-center">
                    Drop a CSV file or click to "Upload CSV" to add multiple assignments at once.<br>
                    Format: Name, Document Type, Document Date, Comments (optional)
                  </p>
                </div>
              </div>
            </div>
          </div>
          <!-- </form> -->
        </div>
        <!-- <aside class="relative flex-1 pt-4 pl-4 pr-4 -my-4 -mr-4 bg-gray-300 border-2 border-gray-600 rounded-r sm:w-full lg:w-1/2 xl:w-2/5 "> -->
        <aside
          class="relative flex flex-col justify-between flex-1 pt-4 pl-4 pr-4 -my-4 -mr-4 bg-secondary-600/40 sm:w-full lg:w-1/2 xl:w-2/5 rounded-r-lg shadow-lg"
        >
          <div>
            <div>
              <h2 class="w-full text-white -my-4 pb-4">
                <div class="flex justify-between text-secondary-900 mx-2">
                  <div class="text-base">Order Summary</div>
                  <div 
                    class="border text-sm self-end cursor-pointer rounded-full px-2 hover:bg-secondary-300" 
                    @click="editOrder = !editOrder"
                    :title="editOrder ? '' : 'Add Attachments / Remove from Order'"
                    :class="{
                      'text-red-700 border-red-700': editOrder,
                      'text-secondary-400 border-secondary-400': !editOrder,
                    }"
                  >
                    {{ editOrder ? 'Cancel Edit' : 'Attachments / Remove Items' }}
                  </div>
                  <div class="text-sm self-end">{{ total | currency }}</div>
                </div>
              </h2>
            </div>
            <div class="pt-2">
              <div
                v-for="(assignment, i) in assignments"
                v-bind:key="assignment.id"
                class="p-2 text-sm bg-white odd:bg-gray-100 border"
                :class="{
                  'rounded-t-lg': i === 0,
                  'rounded-b-lg': i === assignments.length - 1,
                }"
              >
                <div class="flex justify-end pt-0">
                  <!-- <div class="flex">
                                        <div>Urgent: <span v-text="assignment.urgent ? 'Yes' : 'No'"></span></div>
                                        <div class="px-4">Pro: <span v-text="assignment.pro ? 'Yes' : 'No'"></span></div>
                                    </div> -->
                  <div class="actions" v-if="editOrder">
                    <span v-if="i !== showFileUpload" class="pr-4">
                      <i
                        title="add attachment"
                        @click="allowFileUpload(i)"
                        class="cursor-pointer fas fa-paperclip text-logo-alt2 hover:text-logo-alt2-light"
                      ></i>
                    </span>
                    <span
                      ><i
                        title="remove from order"
                        @click="remove(i, assignment.id)"
                        class="text-red-600 cursor-pointer fas fa-trash hover:text-red-800"
                      ></i
                    ></span>
                  </div>
                </div>
                <div class="grid grid-cols-7">
                  <div v-text="assignment.name" class="col-span-2"></div>
                  <div class="col-span-2">{{ assignment.document_type }}
                    <span v-if="assignment.review" class="text-xs italic text-logo">(w/ review)</span>
                    <span v-if="assignment.poc" class="text-xs italic text-logo">(w/ POC)</span>
                  </div>
                  <div
                    v-text="format_date(assignment.date)"
                    class="whitespace-no-wrap col-span-2"
                  ></div>
                  <div class="text-right whitespace-no-wrap">
                    {{ assignment.cost | currency }}
                  </div>
                </div>
                <div
                  v-if="assignment.discount && assignment.discount > 0"
                  class="flex justify-between pt-2"
                >
                  <div class="flex">
                    <div class="pr-2">
                      <strong>Original Cost:</strong>
                    </div>
                    <div>
                      {{ assignment.original_cost | currency }}
                    </div>
                  </div>
                  <div class="flex italic font-thin">
                    <div class="pr-2">
                      <strong>Discount:</strong>
                    </div>
                    <div>- {{ assignment.discount | currency }}</div>
                  </div>
                </div>
                <div v-if="assignment.attachments.length" class="pt-3">
                  <i>Attachments:</i>
                  <div
                    v-for="(doc, docI) in assignment.attachments"
                    v-bind:key="doc.id"
                    class="py-2"
                  >
                    <span class="px-3" title="delete attachment">
                      <i
                        @click="removeAttachment(doc.id, docI, i)"
                        class="text-red-600 cursor-pointer fas fa-trash hover:text-red-800"
                      ></i>
                    </span>
                    <span v-text="doc.original"></span>
                  </div>
                </div>
                <div v-if="i === showFileUpload" class="flex justify-between my-3">
                  <file-upload
                    name="attachments"
                    class="w-full"
                    @loaded="file => handleFileUpload(file, i)"
                  ></file-upload>
                  <div class="mx-3">
                    <button
                      type="button"
                      class="mt-2 text-xs text-red-500 no-underline btn-link hover:underline hover:text-red-800"
                      @click="cancelAttach"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              <!-- <div class="text-right"><button class="mt-4 btn btn-logo" type="button" @click="submitOrder">Submit Order</button></div> -->
            </div>
          </div>
          <div>
            <p class="mt-3 pt-4 text-xs text-white text-justify">
              Clicking submit will place a hold on your card. You will only be charged once your order is completed.
            </p>
            
            <div
              @click="submitOrder"
              class="w-auto p-4 mt-4 -ml-4 -mr-4 text-2xl font-bold text-center text-white cursor-pointer bg-logo hover:bg-secondary-300 hover:text-logo rounded-br-lg shadow-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-xl active:scale-95 active:shadow-md"
            >
              Submit Order
            </div>
          </div>
        </aside>
      </div>
    </div>
    <csv-example-modal 
      :show="showCsvExample"
      :date-formats="dateFormats"
      :document-type-map="documentTypeMap"
      @close="showCsvExample = false"
    />
  </div>
</template>

<script>
import moment from "moment";
import BackButton from "./BackButton.vue";
import FileUpload from "./FileUpload.vue";
import CsvExampleModal from './CsvExampleModal.vue'

export default {
  components: { 
    BackButton, 
    FileUpload,
    CsvExampleModal
  },
  props: ["agency", "card", "orderTotal"],
  data() {
    return {
      showConfirmModal: false,
      charging: false,
      agency_name: this.agency.name,
      agency_id: this.agency.id,
      order_id: this.agency.incomplete_order.id,
      assignments: this.agency.incomplete_order.assignments,
      total: this.orderTotal,
      attachments: "",
      showFileUpload: "",
      fileReady: false,
      assign_data: {
        name: "",
        document_type: "",
        date: "",
        urgent: 0,
        pro: 0,
        review: 0,
        poc: 0,
        comments: "",
      },
      updatePaymentMethod: false,
      stripeConfigs: null,
      last4: null,
      showCardComponent: false,
      showCsvExample: false,
      editOrder: false,
    };
  },
  mounted() {
    // console.log(this.agency);
  },
  methods: {
    assign() {
      if (!this.assign_data.name || !this.assign_data.document_type || !this.assign_data.date) {
        let missing = [];
        if (!this.assign_data.name) missing.push("Name");
        if (!this.assign_data.document_type) missing.push("Document Type");
        if (!this.assign_data.date) missing.push("Date");
        const missingString = missing.length > 1 ? "all required fields" : "the required field";
        this.$toast.error(`Please fill in ${missingString}: ${missing.join(", ")}.`);
        return;
      }
      axios
        .post(
          `/agencies/${this.agency_id}/orders/${this.order_id}/assignments`,
          this.$data.assign_data
        )
        .then(({ data }) => {
          this.reset();
          this.assign_data.review = data.review;
          this.assign_data.poc = data.poc;
          this.assignments.push(data);
          this.total += data.cost;
          this.$toast.success("Assignment added to order.");
        })
        .catch((e) => {
          this.$toast.error(e.response.data.errors[0][0], {
            timeout: 8000
          });
        });
    },
    remove(i, id) {
      axios.delete(`/assignments/${id}`).then(({ data }) => {
        this.assignments.splice(i, 1);
        this.reTotal();
      });
    },
    reTotal() {
      this.total = 0;
      this.assignments.forEach((element) => {
        this.total += element.cost;
      });
    },
    reset() {
      this.assign_data = { name: "", document_type: "", date: "" };
    },
    submitOrder() {
      if (this.assignments.length === 0) {
        this.$toast.error("You must add at least one assignment to your order before submitting.");
        return;
      }
      this.showConfirmModal = true;
      // put the focus on the confirm button
      setTimeout(() => {
        this.$refs.confirmButton.focus();
      }, 100);
    },
    confirmSubmitOrder() {
      this.showConfirmModal = false;
      this.charging = true;
      axios
        .patch(`/agencies/${this.agency_id}/orders/${this.order_id}/submit`)
        .then(({ data }) => {
          window.location.href = "/agencies/" + this.agency_id;
        })
        .catch(({ response }) => {
          this.charging = false;
          this.$toast.error(response.data.message);
          this.updatePaymentMethod = true;
        });
    },
    format_date(d) {
      return moment.utc(d).format("MMM DD, YYYY");
    },
    // addFile(e) {
    //     if (! e.target.files.length) return;

    //     let attachments = e.target.files[0];

    //     let reader = new FileReader();

    //     reader.readAsDataURL(attachments);

    //     reader.onload = e => {
    //         this.assign_data.attachments = e.target.result;
    //     }

    // }
    allowFileUpload(i) {
      this.showFileUpload = i;
    },
    cancelAttach() {
      this.showFileUpload = "";
    },
    handleFileUpload(doc, assignmentIndex) {
      let data = new FormData();
      data.append("attachment", doc.file);
      axios.post(`/assignments/${this.assignments[assignmentIndex].id}/attachments`, data)
        .then(({ data }) => {
          this.assignments[assignmentIndex].attachments.push(data);
          this.$toast.success(`${doc.file.name} uploaded successfully`);
        })
        .catch(error => {
          this.$toast.error(`Failed to upload ${doc.file.name}`);
        })
        .finally(() => {
          // Only close the upload area when all files have been processed
          if (this.assignments[assignmentIndex].attachments.length > 0) {
            this.showFileUpload = "";
          }
        });
    },
    removeAttachment(id, docI, i) {
      axios.delete(`/attachments/${id}`).then(({ data }) => {
        this.assignments[i].attachments.splice(docI, 1);
      });
    },
    triggerUpdatePaymentMethod() {
      this.updatePaymentMethod = false;
      // this.$emit('updatePaymentMethod');
      axios.get(`/agencies/${this.agency_id}/stripe/configs`).then(({ data }) => {
        this.stripeConfigs = data;
        this.showCardComponent = true;
      });
    },
    triggerFileInput() {
      this.$refs.csvInput.value = ''; // Reset the input value
      this.$refs.csvInput.click();
    },
    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file && file.name.toLowerCase().endsWith('.csv')) {
        this.processFile(file);
      } else {
        this.$toast.error('Please drop a CSV file');
      }
    },
    handleCsvUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    processFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => this.parseCsvContent(e.target.result);
      reader.readAsText(file);
    },
    parseCsvContent(content) {
      const lines = content.split(/\r?\n/).filter(line => line.trim());
      if (lines.length === 0) return;

      // Function to parse CSV line respecting quotes
      const parseCSVLine = (line) => {
        const result = [];
        let inQuotes = false;
        let currentValue = '';
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(currentValue.trim());
            currentValue = '';
          } else {
            currentValue += char;
          }
        }
        result.push(currentValue.trim());
        return result;
      };

      // Parse headers and determine column mapping
      const headers = parseCSVLine(lines[0].toLowerCase());
      let nameIndex = -1;
      let typeIndex = -1;
      let dateIndex = -1;
      let commentIndex = -1;
      let hasHeaders = false;

      // Look for column headers using various common variations
      headers.forEach((header, index) => {
        const h = header.replace(/[^a-z]/g, ''); // Remove special chars for matching
        if (h.includes('patient') || h.includes('name') || h.includes('pt')) {
          nameIndex = index;
          hasHeaders = true;
        }
        if (h.includes('type') || h.includes('doc') || h.includes('visit')) {
          typeIndex = index;
          hasHeaders = true;
        }
        if (h.includes('date')) {
          dateIndex = index;
          hasHeaders = true;
        }
        if (h.includes('comment') || h.includes('note')) {
          commentIndex = index;
          hasHeaders = true;
        }
      });

      // If no headers detected, assume standard order
      if (!hasHeaders) {
        nameIndex = 0;
        typeIndex = 1;
        dateIndex = 2;
        commentIndex = 3;
      }

      const startIndex = hasHeaders ? 1 : 0;
      const assignments = [];

      for (let i = startIndex; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length < Math.max(nameIndex, typeIndex, dateIndex) + 1) continue;

        const name = values[nameIndex];
        const docType = values[typeIndex];
        const date = values[dateIndex];
        const comments = commentIndex >= 0 ? values[commentIndex] || '' : '';

        if (!name || !docType || !date) continue;

        const normalizedDocType = this.normalizeDocumentType(docType);
        const normalizedDate = this.normalizeDate(date);

        if (normalizedDocType && normalizedDate) {
          assignments.push({
            name,
            document_type: normalizedDocType,
            date: normalizedDate,
            comments,
            urgent: 0,
            pro: 0,
            review: 0,
            poc: 0
          });
        }
      }

      this.batchAddAssignments(assignments);
    },
    normalizeDocumentType(type) {
      const normalized = type.toLowerCase().trim();
      return this.documentTypeMap[normalized] || null;
    },
    normalizeDate(dateStr) {
      for (const format of this.dateFormats) {
        const parsed = moment(dateStr, format, true);
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD');
        }
      }
      return null;
    },
    async batchAddAssignments(assignments) {
      let successCount = 0;
      let failCount = 0;

      for (const assignment of assignments) {
        try {
          const { data } = await axios.post(
            `/agencies/${this.agency_id}/orders/${this.order_id}/assignments`,
            assignment
          );
          this.assignments.push(data);
          this.total += data.cost;
          successCount++;
        } catch (error) {
          failCount++;
          console.error('Failed to add assignment:', assignment, error);
        }
      }

      if (successCount > 0) {
        this.$toast.success(`Successfully added ${successCount} assignments`);
      }
      if (failCount > 0) {
        this.$toast.error(`Failed to add ${failCount} assignments`);
      }
    }
  },
  computed: {
    dateFormats() {
      return [
        'YYYY-MM-DD',
        'MM/DD/YYYY',
        'MM/DD/YY',
        'M/D/YYYY',
        'M/D/YY',
        'MM-DD-YYYY',
        'MM-DD-YY',
        'MMM DD, YYYY',
        'MMM DD YYYY',
        'MMMM DD, YYYY',
        'MMMM DD YYYY',
      ];
    },
    documentTypeMap() {
      return {
        'soc': 'SOC',
        'start of care': 'SOC',
        'startofcare': 'SOC',
        'roc': 'ROC',
        'resumption': 'ROC',
        'resumption of care': 'ROC',
        'rec': 'Recert',
        'recert': 'Recert',
        'recertification': 'Recert',
        'follow-up': 'Follow-up',
        'follow up': 'Follow-up',
        'followup': 'Follow-up',
        'fu': 'Follow-up',
        'sna': 'SNA',
        'assessment': 'SNA',
        'sn assessment': 'SNA'
      };
    },
    groupedDocTypes() {
      const groups = {};
      Object.entries(this.documentTypeMap).forEach(([input, output]) => {
        if (!groups[output]) {
          groups[output] = [];
        }
        groups[output].push(input);
      });
      return groups;
    }
  },
};
</script>
